import cn from 'classnames';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

type ComparisonTableProps = {
  headers: { text: string }[];
  sections: {
    name: string;
    color: string;
    rows: { cells: { data: string }[] }[];
  }[];
};

const ComparisonDesktop = ({
  tableData: { headers, sections }
}: {
  tableData: ComparisonTableProps;
}) => {
  const hasSectionName = Object.values(sections).some(section => section.name);

  return (
    <table className={cn(hasSectionName ? 'w-[98%]' : 'w-full', 'ml-auto hidden lg:table')}>
      <thead>
        <tr>
          {hasSectionName && <td />}
          <td className="tracking-[4px] text-sm border-b border-neutral-700 text-neutral-10">
            {headers[0].text}
          </td>
          {headers.slice(1, headers.length).map(({ text }, index) => (
            <td
              key={index}
              className={cn(
                { 'pl-0': index === 0, 'pr-0': index === headers.length - 2 },
                'px-4 py-0 lg:w-72 xl:w-[400px]'
              )}
            >
              <div className="rounded-t-lg bg-gradient-x px-[1px] pt-[1px] text-neutral-10">
                <div className="uppercase bg-neutral-800 tracking-[4px] text-sm border-b border-neutral-700 rounded-t-lg text-center py-4 text-neutral-200">
                  <ReactMarkdown rehypePlugins={[rehypeRaw]}>{text}</ReactMarkdown>
                </div>
              </div>
            </td>
          ))}
        </tr>
      </thead>

      <tbody>
        {Object.values(sections).map((section, sectionsIndex) =>
          section.rows.map(({ cells }, trIndex) => (
            <>
              {trIndex === 0 && section.name && (
                <td
                  colSpan={1}
                  rowSpan={section.rows.length + 1}
                  className="relative -translate-x-6"
                >
                  <div
                    className={`bg-${section.color} w-[1px] left-1/2 -translate-x-1/2 absolute inset-0`}
                  />
                  <p
                    className={`text-${section.color} -rotate-90 absolute left-1/2 -translate-x-1/2 bg-black top-1/2 -translate-y-1/2 tracking-[4px] px-3 uppercase`}
                  >
                    {section.name}
                  </p>
                </td>
              )}

              <tr>
                {cells.map(({ data }, tdIndex) => {
                  if (tdIndex === 0)
                    return (
                      <td key={tdIndex} className="py-0 border-b border-neutral-700 text-xl">
                        {data}
                      </td>
                    );

                  return (
                    <td
                      key={tdIndex}
                      className={cn(
                        { 'pl-0': tdIndex === 1, 'pr-0': tdIndex === headers.length - 1 },
                        'px-4 py-0 lg:w-72 xl:w-[400px] h-24'
                      )}
                    >
                      <div
                        className={cn(
                          {
                            'rounded-b-lg pb-[1px]':
                              sectionsIndex === sections.length - 1 &&
                              trIndex === section.rows.length - 1
                          },
                          'bg-gradient-x px-[1px] h-full'
                        )}
                      >
                        <div
                          className={cn(
                            {
                              'rounded-b-lg':
                                sectionsIndex === sections.length - 1 &&
                                trIndex === section.rows.length - 1
                            },
                            'bg-neutral-900 py-6 text-center border-b border-neutral-700 px-6 xl:text-lg 2xl:text-xl h-full flex justify-center items-center'
                          )}
                        >
                          <ReactMarkdown rehypePlugins={[rehypeRaw]}>{data}</ReactMarkdown>
                        </div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            </>
          ))
        )}
      </tbody>
    </table>
  );
};

const ComparisonMobile = ({
  tableData: { headers, sections }
}: {
  tableData: ComparisonTableProps;
}) => {
  const _sections = Object.values(sections);
  const hasSectionName = Object.values(sections).some(section => section.name);

  return (
    <div className="lg:hidden">
      {hasSectionName && (
        <div className="space-x-10 pb-8 mx-auto max-w-max">
          {Object.values(_sections).map(({ name, color }: { name: string; color: string }, idx) => (
            <p key={idx} className={`text-${color} text-sm tracking-[4px] uppercase inline-block`}>
              <span className={`bg-${color} w-2 h-2 rounded-full inline-block mr-2`} />
              {name}
            </p>
          ))}
        </div>
      )}
      <div className="space-y-12">
        {_sections.slice(0, _sections.length).map(({}, index) => (
          <div key={index} className="rounded-lg bg-gradient-x p-[1px]">
            <p className="bg-neutral-800 rounded-t-lg py-4 text-sm text-center border-b border-neutral-700 uppercase tracking-[4px]">
              {headers[index + 1].text}
            </p>
            <div>
              {_sections
                .slice(0, _sections.length)
                .reduce((acc, { color, rows }) => [...acc, { rows, color }], [])
                .map(({ rows, color }) =>
                  rows.map(({ cells }, idx) => (
                    <div
                      key={idx}
                      className="bg-neutral-900 border-b border-neutral-700 last:border-b-0 last:rounded-b-lg px-12 py-4 text-center"
                    >
                      <p className={`text-${color} uppercase text-sm tracking-[4px]`}>
                        {cells[0]?.data}
                      </p>
                      <ReactMarkdown className="text-lg" rehypePlugins={[rehypeRaw]}>
                        {cells[index + 1]?.data}
                      </ReactMarkdown>
                    </div>
                  ))
                )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ComparisonTable = ({ headers, sections }: ComparisonTableProps) => (
  <>
    <ComparisonDesktop tableData={{ headers, sections }} />
    <ComparisonMobile tableData={{ headers, sections }} />
  </>
);

export default ComparisonTable;
